import {
  AccountData,
  OrderData,
  ResourceCalculationMode,
  ResourceHoldData,
  SchedulerConfigurationData,
} from '../../api';

const accountConfigs = (account: AccountData): SchedulerConfigurationData[] =>
  account.shared_resources?.scheduler_configs ?? [];

const configAllowsPage = (config: SchedulerConfigurationData, orderPageId: string): boolean =>
  (config.allowed_masks ?? []).some((mask) => mask.order_page_id === orderPageId);

export const orderHolds = (order: OrderData): ResourceHoldData[] => order.manual_resource_holds ?? [];

export const isManuallySeated = (order: OrderData): boolean => orderHolds(order).length > 0;

export const holdForConfig = (order: OrderData, configId: string): ResourceHoldData | undefined =>
  orderHolds(order).find((hold) => hold.scheduler_config_id === configId);

export const pageConfigOf = (order: OrderData, account: AccountData): SchedulerConfigurationData | undefined =>
  accountConfigs(account).find((config) => configAllowsPage(config, order.order_page_id));

export const seatedIndexesInConfig = (order: OrderData, config: SchedulerConfigurationData): number[] => {
  if (isManuallySeated(order)) {
    return holdForConfig(order, config.id)?.resource_indexes ?? [];
  }
  return configAllowsPage(config, order.order_page_id) ? order.resource_indexes ?? [] : [];
};

export const seatedConfigIds = (order: OrderData, account: AccountData): string[] => {
  if (isManuallySeated(order)) {
    return orderHolds(order).map((hold) => hold.scheduler_config_id);
  }
  const pageConfig = pageConfigOf(order, account);
  return pageConfig ? [pageConfig.id] : [];
};

export const seatedTableCount = (order: OrderData): number =>
  isManuallySeated(order)
    ? orderHolds(order).reduce((sum, hold) => sum + hold.resource_indexes.length, 0)
    : (order.resource_indexes ?? []).length;

export const pageUsesStaticAllocation = (order: OrderData, account: AccountData): boolean =>
  pageConfigOf(order, account)?.allowed_masks.find((mask) => mask.order_page_id === order.order_page_id)
    ?.resource_calculation_mode === ResourceCalculationMode.STATIC;

export const canonicalHolds = (holds: ResourceHoldData[]): ResourceHoldData[] =>
  holds
    .map((hold) => ({ ...hold, resource_indexes: [...hold.resource_indexes].sort((first, second) => first - second) }))
    .sort((first, second) => first.scheduler_config_id.localeCompare(second.scheduler_config_id));
